import React, { useEffect, useState, useCallback } from "react";
import api, { imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Users, FileText, Boxes, Inbox, Plus, Trash2, Upload, X, Filter, Link as LinkIcon, KeyRound, Languages, Video } from "lucide-react";

const TABS = [
  { id: "customers", label: "Customers", icon: Users },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "catalog", label: "Catalog", icon: Boxes },
  { id: "contacts", label: "Messages", icon: Inbox },
  { id: "account", label: "Account", icon: KeyRound },
];

const input = "w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500";
const btn = "rounded-none bg-blue-700 px-4 py-2 text-sm font-bold uppercase tracking-wider text-white hover:bg-blue-800 disabled:opacity-60";
const label = "mb-1 block text-xs font-bold uppercase tracking-wider text-slate-600";

export default function Admin() {
  const [tab, setTab] = useState("customers");
  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="admin-page">
      <h1 className="text-3xl font-bold tracking-tight text-slate-950">Admin Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Manage customers, technical documentation, catalog and messages.</p>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200" data-testid="admin-tabs">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          return (
            <button key={tb.id} onClick={() => setTab(tb.id)} className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${tab === tb.id ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`} data-testid={`admin-tab-${tb.id}`}>
              <Icon className="h-4 w-4" /> {tb.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {tab === "customers" && <Customers />}
        {tab === "documents" && <Documents />}
        {tab === "catalog" && <Catalog />}
        {tab === "contacts" && <Contacts />}
        {tab === "account" && <ChangePassword />}
      </div>
    </div>
  );
}

function Customers() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/customers").then(({ data }) => setList(data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/customers", form);
      toast.success("Customer created");
      setForm({ name: "", email: "", company: "", password: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/admin/customers/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
      <form onSubmit={create} className="space-y-3 border border-slate-200 bg-white p-6" data-testid="customer-form">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-950">New Customer</h3>
        <div><label className={label}>Name</label><input required className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
        <div><label className={label}>Email</label><input required type="email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" /></div>
        <div><label className={label}>Company</label><input className={input} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="cust-company" /></div>
        <div><label className={label}>Password</label><input required className={input} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cust-password" /></div>
        <button className={btn} disabled={busy} data-testid="cust-create-btn"><span className="inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Create</span></button>
      </form>

      <div className="lg:col-span-2 overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full text-left" data-testid="customers-table">
          <thead><tr className="border-b-2 border-slate-800 text-xs uppercase tracking-wider text-slate-600">
            <th className="px-5 py-3 font-bold">Name</th><th className="px-5 py-3 font-bold">Email</th><th className="px-5 py-3 font-bold">Company</th><th className="px-5 py-3"></th></tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-b border-slate-200 text-sm" data-testid={`customer-${c.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                <td className="px-5 py-3 text-slate-600">{c.email}</td>
                <td className="px-5 py-3 text-slate-600">{c.company || "—"}</td>
                <td className="px-5 py-3 text-right"><button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-cust-${c.id}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Documents() {
  const [docs, setDocs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "", title: "", machine: "", landing_url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filterCustomer, setFilterCustomer] = useState("");
  const load = useCallback(() => {
    api.get("/admin/documents").then(({ data }) => setDocs(data));
    api.get("/admin/customers").then(({ data }) => setCustomers(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!file && !form.landing_url.trim()) { toast.error("Provide a file or a landing page URL (at least one)"); return; }
    setBusy(true);
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("customer_id", form.customer_id);
    fd.append("title", form.title);
    fd.append("machine", form.machine);
    fd.append("landing_url", form.landing_url);
    try {
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setForm({ customer_id: "", title: "", machine: "", landing_url: "" });
      setFile(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/admin/documents/${id}`); toast.success("Deleted"); load(); };
  const custName = (id) => customers.find((c) => c.id === id)?.name || id;
  const visibleDocs = filterCustomer ? docs.filter((d) => d.customer_id === filterCustomer) : docs;

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
      <form onSubmit={upload} className="space-y-3 border border-slate-200 bg-white p-6" data-testid="document-form">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-950">Upload Document</h3>
        <div><label className={label}>Customer</label>
          <select required className={input} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="doc-customer">
            <option value="">— Select —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ""}</option>)}
          </select>
        </div>
        <div><label className={label}>Title</label><input required className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" /></div>
        <div><label className={label}>Machine / Model</label><input className={input} value={form.machine} onChange={(e) => setForm({ ...form, machine: e.target.value })} data-testid="doc-machine" /></div>
        <div><label className={label}>Landing page URL</label><input type="url" placeholder="https://…" className={input} value={form.landing_url} onChange={(e) => setForm({ ...form, landing_url: e.target.value })} data-testid="doc-landing" /></div>
        <div><label className={label}>File (optional)</label><input type="file" className={input} onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file" /></div>
        <p className="text-xs text-slate-400">Provide at least one: a downloadable file or a landing page URL.</p>
        <button className={btn} disabled={busy} data-testid="doc-upload-btn"><span className="inline-flex items-center gap-1"><Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload"}</span></button>
      </form>

      <div className="lg:col-span-2 space-y-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select className={`${input} max-w-xs`} value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)} data-testid="doc-filter-customer">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ""}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto border border-slate-200 bg-white">
          <table className="w-full text-left" data-testid="admin-documents-table">
            <thead><tr className="border-b-2 border-slate-800 text-xs uppercase tracking-wider text-slate-600">
              <th className="px-5 py-3 font-bold">Title</th><th className="px-5 py-3 font-bold">Customer</th><th className="px-5 py-3 font-bold">Machine</th><th className="px-5 py-3 font-bold">Landing</th><th className="px-5 py-3"></th></tr></thead>
            <tbody>
              {visibleDocs.map((d) => (
                <tr key={d.id} className="border-b border-slate-200 text-sm" data-testid={`admin-doc-${d.id}`}>
                  <td className="px-5 py-3"><div className="font-medium text-slate-900">{d.title}</div><div className="font-mono text-xs text-slate-400">{d.original_filename || "— (link only)"}</div></td>
                  <td className="px-5 py-3 text-slate-600">{custName(d.customer_id)}</td>
                  <td className="px-5 py-3 text-slate-600">{d.machine || "—"}</td>
                  <td className="px-5 py-3">{d.landing_url ? <a href={d.landing_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline" data-testid={`doc-landing-link-${d.id}`}><LinkIcon className="h-3.5 w-3.5" /> Open</a> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-5 py-3 text-right"><button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-doc-${d.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
              {visibleDocs.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">No documents.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const EMPTY_CAT = { category: "assembly", name: "", model: "", description: "", features: "", image_url: "", pdf_filename: "", videos: "", order: 0, source_lang: "en" };

function Catalog() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY_CAT);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [retranslating, setRetranslating] = useState(false);
  const load = useCallback(() => api.get("/catalog").then(({ data }) => setList(data)), []);
  useEffect(() => { load(); }, [load]);

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/catalog/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image_url: data.filename }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/catalog/upload-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, pdf_filename: data.filename }));
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPdf(false);
    }
  };


  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, order: Number(form.order) || 0, features: form.features.split("\n").map((f) => f.trim()).filter(Boolean), video_urls: form.videos.split("\n").map((v) => v.trim()).filter(Boolean) };
    delete payload.videos;
    try {
      if (editId) await api.put(`/admin/catalog/${editId}`, payload);
      else await api.post("/admin/catalog", payload);
      toast.success(editId ? "Updated" : "Created");
      setForm(EMPTY_CAT); setEditId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const edit = (it) => { setEditId(it.id); setForm({ ...EMPTY_CAT, ...it, source_lang: it.source_lang || "en", features: (it.features || []).join("\n"), videos: (it.video_urls || []).join("\n") }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditId(null); setForm(EMPTY_CAT); };
  const remove = async (id) => { await api.delete(`/admin/catalog/${id}`); toast.success("Deleted"); load(); };

  const retranslateAll = async () => {
    setRetranslating(true);
    try {
      const { data } = await api.post("/admin/catalog/retranslate-all");
      toast.success(`Translated ${data.translated}/${data.total} items`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Translation failed");
    } finally {
      setRetranslating(false);
    }
  };

  return (
    <div className="space-y-10">
      <CategoryVideos />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
      <form onSubmit={save} className="space-y-3 border border-slate-200 bg-white p-6" data-testid="catalog-form">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-950">{editId ? "Edit Item" : "New Item"}</h3>
          {editId && <button type="button" onClick={cancel} className="text-slate-400 hover:text-slate-900" data-testid="cat-cancel"><X className="h-4 w-4" /></button>}
        </div>
        <div><label className={label}>Category</label>
          <select className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="cat-category">
            <option value="assembly">Assembly</option><option value="cutting">Cutting</option><option value="soldering">Soldering</option>
          </select>
        </div>
        <div><label className={label}>Content language (what you type below)</label>
          <select className={input} value={form.source_lang} onChange={(e) => setForm({ ...form, source_lang: e.target.value })} data-testid="cat-source-lang">
            <option value="en">English</option><option value="it">Italiano</option><option value="fr">Français</option><option value="de">Deutsch</option><option value="es">Español</option>
          </select>
          <p className="mt-1 text-xs text-slate-400">The other languages are translated automatically by AI when you save.</p>
        </div>
        <div><label className={label}>Name</label><input required className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" /></div>
        <div><label className={label}>Model</label><input className={input} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="cat-model" /></div>
        <div><label className={label}>Description</label><textarea rows={3} className={input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cat-desc" /></div>
        <div><label className={label}>Features (one per line)</label><textarea rows={3} className={input} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="cat-features" /></div>
        <div>
          <label className={label}>Image — upload file</label>
          <input type="file" accept="image/*" className={input} onChange={uploadImage} data-testid="cat-image-file" />
          {uploading && <p className="mt-1 text-xs text-slate-400">Uploading…</p>}
          {form.image_url && <img src={imageUrl(form.image_url)} alt="" className="mt-2 h-20 w-28 border border-slate-200 object-cover" data-testid="cat-image-preview" />}
        </div>
        <div><label className={label}>…or paste Image URL</label><input className={input} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="cat-image" /></div>
        <div>
          <label className={label}>Datasheet PDF (optional)</label>
          <input type="file" accept="application/pdf" className={input} onChange={uploadPdf} data-testid="cat-pdf-file" />
          {uploadingPdf && <p className="mt-1 text-xs text-slate-400">Uploading…</p>}
          {form.pdf_filename && <p className="mt-1 inline-flex items-center gap-1 text-xs text-green-600" data-testid="cat-pdf-status"><FileText className="h-3.5 w-3.5" /> PDF attached</p>}
        </div>
        <div>
          <label className={label}>Video URLs — YouTube / Vimeo (one per line)</label>
          <textarea rows={3} placeholder="https://www.youtube.com/watch?v=…" className={input} value={form.videos} onChange={(e) => setForm({ ...form, videos: e.target.value })} data-testid="cat-videos" />
          <p className="mt-1 text-xs text-slate-400">Shown on the machine detail page. A badge appears on the catalog card.</p>
        </div>
        <div><label className={label}>Order</label><input type="number" className={input} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="cat-order" /></div>
        <div className="flex gap-2">
          <button className={btn} disabled={busy} data-testid="cat-save-btn">{busy ? "Translating…" : (editId ? "Save" : "Create")}</button>
          {editId && <button type="button" onClick={cancel} disabled={busy} className="rounded-none border border-slate-300 px-4 py-2 text-sm font-bold uppercase tracking-wider text-slate-700 hover:border-slate-900 hover:text-slate-900 disabled:opacity-60" data-testid="cat-cancel-btn">Cancel</button>}
        </div>
      </form>

      <div className="lg:col-span-2 space-y-3">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500">{list.length} items</span>
          <button onClick={retranslateAll} disabled={retranslating} className="inline-flex items-center gap-1 rounded-none border border-blue-700 px-4 py-2 text-xs font-bold uppercase tracking-wider text-blue-700 transition-colors hover:bg-blue-700 hover:text-white disabled:opacity-60" data-testid="retranslate-all-btn">
            <Languages className="h-4 w-4" /> {retranslating ? "Translating…" : "Translate all"}
          </button>
        </div>
        {list.map((it) => (
          <div key={it.id} className="flex items-center gap-4 border border-slate-200 bg-white p-4" data-testid={`cat-row-${it.id}`}>
            {it.image_url ? <img src={imageUrl(it.image_url)} alt={it.name} className="h-16 w-20 shrink-0 object-cover" /> : <div className="flex h-16 w-20 shrink-0 items-center justify-center bg-slate-100 text-slate-300"><Boxes className="h-6 w-6" /></div>}
            <div className="flex-1">
              <div className="font-mono text-xs uppercase text-slate-400">{it.category} · {it.model}</div>
              <div className="font-bold text-slate-900">{it.name}</div>
            </div>
            <button onClick={() => edit(it)} className="text-xs font-bold uppercase tracking-wider text-blue-700 hover:underline" data-testid={`edit-cat-${it.id}`}>Edit</button>
            <button onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-cat-${it.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

const CATEGORIES = [
  { id: "assembly", label: "Assembly" },
  { id: "cutting", label: "Cutting" },
  { id: "soldering", label: "Soldering" },
];

function CategoryVideos() {
  const [videos, setVideos] = useState({ assembly: "", cutting: "", soldering: "" });
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/catalog/category-videos").then(({ data }) => {
      setVideos({
        assembly: (data.assembly || []).join("\n"),
        cutting: (data.cutting || []).join("\n"),
        soldering: (data.soldering || []).join("\n"),
      });
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (cat) => {
    setBusy(cat);
    try {
      const urls = videos[cat].split("\n").map((v) => v.trim()).filter(Boolean);
      await api.put(`/admin/category-videos/${cat}`, { video_urls: urls });
      toast.success("Category videos saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(""); }
  };

  return (
    <div className="border border-slate-200 bg-white p-6" data-testid="category-videos">
      <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-950"><Video className="h-4 w-4" /> Category Videos</h3>
      <p className="mt-1 text-xs text-slate-500">Videos shown at the top of each category section on the public catalog. Paste YouTube/Vimeo links, one per line.</p>
      <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-3">
        {CATEGORIES.map((c) => (
          <div key={c.id}>
            <label className={label}>{c.label}</label>
            <textarea rows={3} placeholder="https://www.youtube.com/watch?v=…" className={input} value={videos[c.id]} onChange={(e) => setVideos({ ...videos, [c.id]: e.target.value })} data-testid={`catvid-${c.id}`} />
            <button type="button" onClick={() => save(c.id)} disabled={busy === c.id} className="mt-2 rounded-none border border-blue-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 hover:bg-blue-700 hover:text-white disabled:opacity-60" data-testid={`catvid-save-${c.id}`}>{busy === c.id ? "Saving…" : "Save"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Contacts() {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/admin/contacts").then(({ data }) => setList(data)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { await api.delete(`/admin/contacts/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-4" data-testid="contacts-list">
      {list.map((c) => (
        <div key={c.id} className="border border-slate-200 bg-white p-5" data-testid={`contact-msg-${c.id}`}>
          <div className="flex items-start justify-between">
            <div>
              <div className="font-bold text-slate-900">{c.name} {c.company ? <span className="font-normal text-slate-500">· {c.company}</span> : null}</div>
              <div className="text-sm text-blue-700">{c.email}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-slate-400">{(c.created_at || "").slice(0, 10)}</span>
              <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-msg-${c.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          {c.subject && <div className="mt-2 text-sm font-medium text-slate-800">{c.subject}</div>}
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{c.message}</p>
        </div>
      ))}
      {list.length === 0 && <p className="py-16 text-center text-slate-400">No messages yet.</p>}
    </div>
  );
}


function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md">
      <form onSubmit={submit} className="space-y-4 border border-slate-200 bg-white p-6" data-testid="change-password-form">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-950">Change Password</h3>
        <p className="text-xs text-slate-500">Update the password for your account.</p>
        <div><label className={label}>Current password</label><input required type="password" className={input} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="cp-current" /></div>
        <div><label className={label}>New password</label><input required type="password" className={input} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="cp-new" /><p className="mt-1 text-xs text-slate-400">At least 8 characters.</p></div>
        <div><label className={label}>Confirm new password</label><input required type="password" className={input} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="cp-confirm" /></div>
        <button className={btn} disabled={busy} data-testid="cp-submit-btn">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </div>
  );
}
